import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchBar } from '../components/SearchBar';
import { Loader2, Plane, Home, PiggyBank, Receipt, Lock } from 'lucide-react';
import { useBlogStore } from '../store';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { BlogCard } from "../components/BlogCard";

const getHeaderIcon = (slug: string) => {
  switch (slug) {
    case 'when-to-book-flights-abroad':
      return <Plane className="w-12 h-12 text-gray-800" />;
    case 'when-to-move-apartments':
      return <Home className="w-12 h-12 text-gray-800" />;
    case 'when-to-start-saving-for-pension':
      return <PiggyBank className="w-12 h-12 text-gray-800" />;
    case 'when-to-organize-household-expenses':
      return <Receipt className="w-12 h-12 text-gray-800" />;
    default:
      return null;
  }
};

const getHeaderAltText = (slug: string) => {
  switch (slug) {
    case 'when-to-book-flights-abroad':
      return 'איור מינימליסטי של מטוס - מתי כדאי להזמין טיסות';
    case 'when-to-move-apartments':
      return 'איור מינימליסטי של בית - מתי כדאי לעבור דירה';
    case 'when-to-start-saving-for-pension':
      return 'איור מינימליסטי של קופת חיסכון - מתי כדאי להתחיל לחסוך לפנסיה';
    case 'when-to-organize-household-expenses':
      return 'איור מינימליסטי של חשבונית - מתי כדאי לעשות סדר בהוצאות הבית';
    default:
      return '';
  }
};

export const Blog: React.FC = () => {
  const { posts, loading, error, fetchPosts } = useBlogStore();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    fetchPosts();

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [fetchPosts]);

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isAdmin = user?.email === 'meitalbenshabat2@gmail.com';

  return (
    <div className="max-w-4xl mx-auto py-12 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">בלוג מה כדאי</h1>
        {!user && (
          <Link 
            to="/admin"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <Lock className="w-5 h-5" />
            <span>ניהול</span>
          </Link>
        )}
      </div>
      
      <div className="mb-8">
        <SearchBar 
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="חיפוש במאמרים..."
        />
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : error ? (
        <div className="text-center text-red-600 py-12">
          {error}
        </div>
      ) : (
        <div className="grid gap-8">
          {filteredPosts.map(post => (
            <BlogCard 
              key={post.id} 
              post={post} 
              icon={getHeaderIcon(post.slug)}
              iconAlt={getHeaderAltText(post.slug)}
            />
          ))}
          
          {filteredPosts.length === 0 && (
            <div className="text-center text-gray-500 py-12">
              {searchQuery.trim() ? 'לא נמצאו תוצאות לחיפוש זה' : 'אין פוסטים עדיין'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};